import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataGrid, List, ScrollArea } from "../../../../components";
import { getRequest, queries } from "../../../../react-query";
import { useQuery } from "react-query";
import ReactPaginate from "react-paginate";
import { TriangleLeftIcon, TriangleRightIcon } from "@radix-ui/react-icons";
import { useLocation, useNavigate } from "react-router-dom";
import { GlobalFilters, GlobalSearch } from "../../../../components/filters";
import { reverseArray } from "../../../../utils";


interface VideoRow {
  client: {
    company_name: string;
    full_name: string
  };
  // Add other properties here if needed
}

export const VideosList = () => {
  let size = 10;
  const navigate = useNavigate();
  const [page, setPage] = useState<number>(1);

  const [input, setInput] = useState({
    filter: "ACTIVE",
    search: "",
  });

  const {
    data: videosData = [],
    isLoading,
    error,
    refetch,
  }: any = useQuery(
    [queries.videos, input.filter, page, size],
    () =>
      getRequest(`/video?status=${input.filter}&page=${page}&pageSize=${size}`),
    {
      enabled: !!page || !!input.filter,
    }
  );

  // Destructuring Data
  const { data: videos = [], pagination } = videosData;

  const totalPages = useMemo(
    () => Math.ceil(pagination?.totalCount / pagination?.pageSize),
    [pagination]
  );

  const onPageChangeHandler = useCallback((page: number) => {
    setPage(page + 1);
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "name",
        disableFilters: true,
      },
      {
        Header: "Description",
        accessor: "description",
        disableFilters: true,
      },
      {
        Header: "Client",
        accessor: (row:VideoRow) => `${row.client.company_name} (${row.client.full_name})`,
        disableFilters: true,
      },
      {
        Header: "Location",
        accessor: "location.name",
        disableFilters: true,
      },
      {
        Header: "Categories",
        maxWidth: 200,
        accessor: (row: any) => {
          const formatted = reverseArray(row.sub_categories)?.map(
            (category: any) => {
              const subCategoryNames = category.sub_categories
                ?.map((sub: any) => sub.name)
                .join(", ");
              return `${category.name} - ${subCategoryNames}`;
            }
          );
          return JSON.stringify(formatted);
        },
        disableFilters: true,
      },
    ],
    [videos]
  );

  const onValueChangeHandler = (value: string, name: string) => {
    if (value) {
      setInput({
        ...input,
        [name]: value,
      });
    }
  };

  const onRowHandler = (data: any) => {
    navigate(`/settings/video/update/${data.id}`, {
      state: {
        data: data,
      },
    });
  };

  const goToPage = () => {
    navigate("/settings/video/create");
  };

  return (
    <div className="text-black space-y-4 py-4">
      <div className="flex justify-between items-center">
        <GlobalFilters
          filter={input["filter"]}
          setFilter={(value: string) => onValueChangeHandler(value, "filter")}
        />
        <div className="flex items-center space-x-4">
          <GlobalSearch
            value={input["search"]}
            setValue={(e: any) =>
              onValueChangeHandler(e.target.value, "search")
            }
          />
          <button
            onClick={goToPage}
            className="bg-green active:bg-[#228B22] px-4 py-1.5 text-white rounded-full"
          >
            Create
          </button>
        </div>
      </div>
      <ScrollArea className="w-full  h-[calc(100vh-260px)]">
        <DataGrid data={videos} columns={columns} onRowHandler={onRowHandler} />
      </ScrollArea>

      <ReactPaginate
        containerClassName={"paginationContainer"}
        pageClassName={"paginationItem"}
        activeClassName={"paginationActive"}
        onPageChange={(event) => onPageChangeHandler(event.selected)}
        pageCount={totalPages}
        breakLabel="..."
        previousLabel={
          <TriangleLeftIcon className={`w-10 h-10 cursor-pointer`} />
        }
        nextLabel={<TriangleRightIcon className={`w-10 h-10 cursor-pointer`} />}
      />
    </div>
  );
};
